import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listAssessments } from '../services/api';
import { useAuth } from '../context/AuthContext';
import type { AssessmentListItem } from '../types/api';
import { ProjectStatusDistribution } from '../components/dashboard/ProjectStatusDistribution';
import { RecentProjectsTable } from '../components/dashboard/RecentProjectsTable';
import { KpiDonuts } from '../components/dashboard/KpiDonuts';

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    completed: 'badge-pass',
    analysing: 'badge-watch',
    ingesting: 'badge-neutral',
    failed: 'badge-fail',
    cancelled: 'badge-neutral',
  };
  const label: Record<string, string> = {
    completed: 'Completed',
    analysing: 'Analysing',
    ingesting: 'Ingesting',
    failed: 'Failed',
    cancelled: 'Cancelled',
  };
  return (
    <span className={`badge ${map[status] ?? 'badge-neutral'}`}>
      {status === 'ingesting' || status === 'analysing' ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span className="spinner" style={{ width: 8, height: 8 }} />
          {label[status] ?? status}
        </span>
      ) : (label[status] ?? status)}
    </span>
  );
}
void StatusBadge;

const PAGE_SIZE = 20;

/* Compact pagination range: first / current ±1 / last with 'ellipsis'
   tokens. Returns 0-indexed page numbers. */
function buildPageRange(current: number, total: number): Array<number | 'ellipsis'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const out: Array<number | 'ellipsis'> = [];
  const last = total - 1;
  const lo = Math.max(1, current - 1);
  const hi = Math.min(last - 1, current + 1);
  out.push(0);
  if (lo > 1) out.push('ellipsis');
  for (let p = lo; p <= hi; p++) out.push(p);
  if (hi < last - 1) out.push('ellipsis');
  out.push(last);
  return out;
}
// Sprint 5 — helpers retained for the hidden legacy table; void
// marks them used so tsc strict-unused doesn't complain.
void relativeTime; void buildPageRange;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ModuleBadge({ type }: { type: string }) {
  return (
    <span className={`badge ${type === 'SPA' ? 'badge-spa' : 'badge-dd'}`} style={{ fontSize: '0.7rem' }}>
      {type}
    </span>
  );
}
void ModuleBadge;

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState<AssessmentListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [moduleCounts, setModuleCounts] = useState<Record<string, number>>({});
  const [page, setPage] = useState(0);  // zero-indexed
  // Sprint 5 — Sprint-4 legacy table retired; keep the state so the
  // setters used by the fetcher still type-check.
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  void isLoading; void error; void setPage;

  useEffect(() => {
    setIsLoading(true);
    listAssessments({ limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
        setStatusCounts(res.status_counts ?? {});
        setModuleCounts(res.module_counts ?? {});
      })
      .catch(() => setError('Failed to load assessments'))
      .finally(() => setIsLoading(false));
  }, [page]);

  // Stats use server-side status_counts (full filtered set), not the
  // visible page — so the numbers add up regardless of pagination.
  // The DD / SPA breakdown still needs the page-level items since
  // status_counts is by status, not module_type. To get accurate
  // module-type totals we'd need a separate API field; for now this
  // approximates by showing module breakdown of the current page.
  const completed = statusCounts.completed ?? 0;
  const inProgress = (statusCounts.ingesting ?? 0) + (statusCounts.analysing ?? 0);
  const failed = statusCounts.failed ?? 0;
  const cancelled = statusCounts.cancelled ?? 0;
  // DD/SPA counts from server-side module_counts so the cards reconcile
  // with Total (DD + SPA = Total when all assessments are one of these
  // two module types).
  const ddCount = moduleCounts.DD ?? 0;
  const spaCount = moduleCounts.SPA ?? 0;

  // Sprint 5 — legacy pagination machinery kept inert; void marks them
  // used so tsc strict-unused doesn't complain.
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstShown = items.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastShown = page * PAGE_SIZE + items.length;
  void totalPages; void firstShown; void lastShown;

  const displayName = user?.full_name || user?.email || 'User';
  const displayRole = user?.role || 'Member';
  const avatarInitials = getInitials(displayName);

  // Suppress unused-warning lint for the legacy avatar/role pair after
  // dropping the top center-bar chrome. They still feed the sidebar's
  // user block via AuthContext.
  void displayName; void displayRole; void avatarInitials;

  // UI-SP2-1 (re-opened) — single "+ New Application" button with a
  // workflow picker dropdown. The previous two buttons (New SPA + New
  // DD) didn't match the team's reference design.
  // Sprint 5 — old DD/SPA New Application menu retired. Single
  // "+ New Project" button replaces it. (showNewMenu state removed.)

  // UI-SP2-1 (re-opened) — dashboard filter strip. Filters apply to the
  // Recent Assessments table client-side; server still serves the page.
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');

  const visibleItems = items.filter((it) => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const hay = [it.deal_name ?? '', it.analyst_name ?? '', it.assessment_id].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filterStatus && it.status !== filterStatus) return false;
    if (filterType && it.module_type !== filterType) return false;
    return true;
  });

  return (
    <div>
      {/* Sprint 5 client feedback 2026-06-22 — Dashboard restyle pass 1:
          remove the beige header band (per team design audit). Title row
          now sits directly on the page background with compact padding;
          the visual separation comes from the card stack below, not a
          coloured band above. */}
      <div className="page-topbar page-topbar-compact">
        <div>
          <h1 className="page-topbar-title">Dashboard</h1>
          <p className="page-topbar-sub">Manage and review investor applications</p>
        </div>
        <div className="topbar-actions" style={{ position: 'relative' }}>
          {/* Sprint 5 — DD and SPA can no longer be created standalone;
              every audit starts as a Project. Single "+ New Project"
              CTA replaces the prior DD / SPA menu. */}
          <button
            className="btn btn-accent btn-compact"
            onClick={() => navigate('/projects/new')}
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Project
          </button>
        </div>
      </div>

      <div className="dashboard-header-content">
        {/* Sprint 5 — Sprint-4 "Status Distribution" + DD/SPA mix
            donut retired. The Project Status card at the top of the
            page replaces it. The chart was confusing because it
            double-counts (one standalone DD + the same DD as part
            of a Project = two rows in the donut) and the
            "DD Type / SPA Type" mix doesn't reflect the new Project
            model. Toggle the `false &&` to `true &&` if you need
            to see the legacy chart for verification. */}
        {false && (() => {
          const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);
          const completionRate = total > 0 ? (completed / total) * 100 : 0;
          const ddPct  = pct(ddCount);
          const spaPct = pct(spaCount);
          return (
            <section className="status-panel">
              <div className="status-summary">
                <div className="status-summary-head">
                  <h3>Status Distribution</h3>
                  <div className="status-total">
                    <span className="status-total-num">{total}</span>
                    <span className="status-total-label">Total</span>
                  </div>
                  <p className="status-completion">{completionRate.toFixed(1)}% Completion Rate</p>
                </div>
                <div className="status-bars">
                  <div
                    className="status-bar"
                    role="img"
                    aria-label={`Status distribution: ${completed} completed, ${inProgress} in progress, ${failed} failed, ${cancelled} cancelled`}
                  >
                    <span className="status-seg seg-pass" style={{ width: `${pct(completed)}%` }} />
                    <span className="status-seg seg-warn" style={{ width: `${pct(inProgress)}%` }} />
                    <span className="status-seg seg-fail" style={{ width: `${pct(failed)}%` }} />
                    <span className="status-seg seg-mute" style={{ width: `${pct(cancelled)}%` }} />
                  </div>
                  <ul className="status-legend">
                    <li><span className="dot seg-pass" />{completed} Completed</li>
                    <li><span className="dot seg-warn" />{inProgress} In Progress</li>
                    <li><span className="dot seg-fail" />{failed} Failed</li>
                    <li><span className="dot seg-mute" />{cancelled} Cancelled</li>
                  </ul>
                </div>
              </div>
              <div className="status-divider" />
              <div className="status-types">
                <div className="status-donut-wrap">
                  <svg className="status-donut" viewBox="0 0 36 36" aria-hidden="true">
                    <circle className="ring-track" cx="18" cy="18" r="15.9155" fill="none" strokeWidth="4" />
                    <circle
                      className="ring-dd"
                      cx="18" cy="18" r="15.9155" fill="none" strokeWidth="4"
                      strokeDasharray={`${ddPct} ${100 - ddPct}`}
                      strokeDashoffset="0"
                    />
                    <circle
                      className="ring-spa"
                      cx="18" cy="18" r="15.9155" fill="none" strokeWidth="4"
                      strokeDasharray={`${spaPct} ${100 - spaPct}`}
                      strokeDashoffset={-ddPct}
                    />
                  </svg>
                  <div className="status-donut-center" aria-hidden="true">
                    <strong>{total}</strong>
                    <span>Total</span>
                  </div>
                </div>
                <ul className="status-type-legend">
                  <li>
                    <span className="sw dd" />
                    <div>
                      <span className="type-label">DD Type</span>
                      <span className="type-value">{ddCount}</span>
                    </div>
                  </li>
                  <li>
                    <span className="sw spa" />
                    <div>
                      <span className="type-label">SPA Type</span>
                      <span className="type-value">{spaCount}</span>
                    </div>
                  </li>
                </ul>
              </div>
            </section>
          );
        })()}

        {/* Sprint 5 — Sprint-4 filter strip + assessment table
            retired from the Dashboard. The flat row-per-assessment
            view double-counts when one Project owns two Assessments
            (DD + SPA). The Sprint-5 surface for this is /projects
            (the AuditAIListPage). Toggle `false &&` to re-enable
            temporarily if you need the legacy view back. */}
        {false && (<>
        <div className="filter-strip">
          <div className="filter-search">
            <svg className="filter-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="search"
              className="filter-search-input"
              placeholder="Search by name, ID, analyst..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} aria-label="Status filter">
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="analysing">Processing</option>
            <option value="ingesting">Ingesting</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select className="filter-select" value={filterType} onChange={(e) => setFilterType(e.target.value)} aria-label="Type filter">
            <option value="">All Types</option>
            <option value="DD">DD</option>
            <option value="SPA">SPA</option>
          </select>
          <select className="filter-select" defaultValue="" aria-label="Date filter">
            <option value="">All Dates</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          {/* Sprint 5 client feedback 2026-07-08 (UI Ticket — Reports
              Export removal) — non-functional Export button removed. */}
        </div>

        <p className="t-xs t-muted dashboard-header-counter">
          Showing {visibleItems.length} of {total} assessments
        </p>
      </>)}
      </div>

      <div className="page-body">
        {/* Sprint 5 T119 — Project Status Distribution (dist-card
            3-zone layout matching design/dashboard.html). Hides
            itself silently when no Projects exist yet. */}
        <ProjectStatusDistribution />

        {/* Sprint 5 UI-SP3-2 — KPI donut tiles. 4 circular progress
            rings: Overall completion + Stage 1 DD / Stage 2 SPA /
            Stage 3 Risk. Sits between the Project Status header
            and the Recent Projects table. */}
        <KpiDonuts />

        {/* Recent projects table — primary content on the dashboard
            per the design. The ProjectStatusDistribution card above
            already provides the summary signal. */}
        <RecentProjectsTable />

        {/* Quick links row below the main listing.
            Sprint 5 client feedback 2026-06-22 — Dashboard pass 3:
            tightened top margin var(--s5) → var(--s4) to match
            the rest of the dashboard's denser inter-section
            rhythm. */}
        <div
          style={{
            display: 'flex',
            gap: 'var(--s3)',
            marginTop: 'var(--s4)',
            justifyContent: 'flex-end',
            alignItems: 'center',
            color: 'var(--muted)',
            fontSize: '0.85rem',
          }}
        >
          <span>Looking for pre-Sprint-5 reports?</span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => navigate('/dd/assessments')}
            data-tip="Sprint-4 legacy report listing"
          >
            Legacy Reports →
          </button>
        </div>

      </div>
    </div>
  );
}
